import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import { Stack, Checkbox, Typography, FormControlLabel } from '@mui/material';

import { useTranslate } from 'src/locales';
import {
  EngageMarketingAddResult,
  EngageMarketingPersonType,
  useAddToEngageMarketingListMutation,
} from 'src/services/engage/engage.service';

import { ConfirmDialog } from 'src/components/custom-dialog';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  personType: EngageMarketingPersonType;
  /** OrganizationCandidate ids for candidates, Contact ids for contacts. */
  ids: string[];
  /** Shown in the title when adding one person. */
  personName?: string;
};

/** The one-line summary of what Engage did, for the toast (JT1-1925). */
export function marketingAddSummary(result: EngageMarketingAddResult, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const parts = [t('components.engage_marketing.added', { added: result.added })];
  if (result.already) parts.push(t('components.engage_marketing.already', { count: result.already }));
  if (result.skipped.length) {
    const reasons = Array.from(new Set(result.skipped.map((s) => s.reason))).join('; ');
    parts.push(t('components.engage_marketing.skipped', { count: result.skipped.length, reasons }));
  }
  if (result.over_limit) parts.push(t('components.engage_marketing.over_limit', { count: result.over_limit }));
  return parts.join(' ');
}

/**
 * Adds candidates or contacts to the agency's Engage marketing list, after the recruiter confirms
 * they agreed to marketing emails (JT1-1925). Engage leaves out anyone who unsubscribed and stops at
 * the agency's marketing-contact limit; the toast says what happened.
 */
export default function AddToEngageMarketingDialog({ open, onClose, personType, ids, personName }: Props) {
  const { t } = useTranslate();
  const [consented, setConsented] = useState(false);
  const [add, { isLoading }] = useAddToEngageMarketingListMutation();

  const confirm = async () => {
    try {
      const result = await add({ type: personType, ids }).unwrap();
      const fullyAdded = !result.skipped.length && !result.over_limit;
      enqueueSnackbar(marketingAddSummary(result, t), { variant: fullyAdded ? 'success' : 'warning' });
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || t('components.engage_marketing.failed'), { variant: 'error' });
    }
  };

  const title = ids.length === 1 && personName
    ? t('components.engage_marketing.title_one', { name: personName })
    : t('components.engage_marketing.title_other', { count: ids.length });

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={title}
      content={(
        <Stack spacing={1.5}>
          <Typography variant="body2">{t('components.engage_marketing.content')}</Typography>
          <FormControlLabel
            control={(
              <Checkbox
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                inputProps={{ 'aria-label': t('components.engage_marketing.consent') }}
              />
            )}
            label={t('components.engage_marketing.consent')}
          />
        </Stack>
      )}
      action={(
        <LoadingButton
          variant="contained"
          loading={isLoading}
          disabled={!consented}
          onClick={confirm}
          data-testid="engage-marketing-confirm"
        >
          {t('components.engage_marketing.confirm')}
        </LoadingButton>
      )}
    />
  );
}
